/**
 * Drop-in replacement for `next/head` (aliased in `next.config.js`).
 *
 * Every page, layout and meta component keeps writing `import Head from
 * "next/head"`; this wrapper then brands the tags that matter for the "TU" /
 * tu.biz brand, so new pages get it without remembering:
 *
 *   <title>                 -> "<page> | TU - Technical Union"
 *   og:title / twitter:title -> same
 *   name="description"      -> brand appended when missing and it fits
 *   name="keywords"         -> TU, Technical Union, tu.biz added
 *   property="site_name"    -> og:site_name "TU | Technical Union"
 *
 * Rules live in `utils/brand.js`. Keys and element order are left untouched so
 * Next's own <Head> de-duplication keeps working.
 */
import React from "react";
import { useRouter } from "next/router";
import NextHead from "next/dist/shared/lib/head";
import {
	BRAND_SITE_NAME,
	brandDescription,
	brandKeywords,
	brandTitle,
	isHomePath,
} from "../utils/brand";

function titleText(children) {
	return React.Children.toArray(children)
		.filter((c) => typeof c === "string" || typeof c === "number")
		.join("");
}

function brandElement(el, isHome) {
	const { type, props } = el;

	if (type === "title") {
		return React.cloneElement(el, undefined, brandTitle(titleText(props.children), { isHome }));
	}

	if (type !== "meta" || typeof props.content !== "string") return el;

	if (props.name === "description") {
		return React.cloneElement(el, { content: brandDescription(props.content) });
	}
	if (props.name === "keywords") {
		return React.cloneElement(el, { content: brandKeywords(props.content) });
	}
	if (props.property === "og:title" || props.name === "twitter:title") {
		return React.cloneElement(el, { content: brandTitle(props.content, { isHome }) });
	}
	if (props.property === "site_name" || props.property === "og:site_name") {
		return React.cloneElement(el, {
			key: "og-site-name",
			property: "og:site_name",
			content: BRAND_SITE_NAME,
		});
	}

	return el;
}

function walk(node, isHome) {
	if (Array.isArray(node)) return node.map((child) => walk(child, isHome));
	if (!React.isValidElement(node)) return node;
	if (node.type === React.Fragment) {
		return withChildren(node, walk(node.props.children, isHome));
	}
	return brandElement(node, isHome);
}

// Children passed as arguments (not as an array prop) are "static", so React does
// not ask for keys on JSX that never had them. Keyed lists stay arrays inside.
function withChildren(node, children) {
	return Array.isArray(children)
		? React.cloneElement(node, undefined, ...children)
		: React.cloneElement(node, undefined, children);
}

export default function BrandedHead({ children }) {
	const router = useRouter();
	const isHome = isHomePath(router?.pathname);
	return withChildren(<NextHead />, walk(children, isHome));
}
